import { useState } from 'react';
import {
  Copy,
  Download,
  FileSpreadsheet,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  runExport,
  useCreateTemplate,
  useDeleteTemplate,
  type ExportTemplate,
} from '@/lib/export';
import { usePermission } from '@/lib/permissions';

interface TemplateListProps {
  templates: ExportTemplate[];
  onNew: () => void;
  onEdit: (template: ExportTemplate) => void;
}

export function TemplateList({ templates, onNew, onEdit }: TemplateListProps) {
  const [query, setQuery] = useState('');
  const match = (t: ExportTemplate) => t.name.toLowerCase().includes(query.trim().toLowerCase());
  const personal = templates.filter((t) => t.scope === 'personal' && match(t));
  const shared = templates.filter((t) => t.scope === 'shared' && match(t));
  const noMatches = templates.length > 0 && personal.length === 0 && shared.length === 0;
  const [pendingDelete, setPendingDelete] = useState<ExportTemplate | null>(null);

  const createMut = useCreateTemplate();
  const deleteMut = useDeleteTemplate();

  const duplicate = async (t: ExportTemplate) => {
    try {
      await createMut.mutateAsync({
        name: `${t.name} (copy)`,
        scope: 'personal',
        format: t.format,
        fields: t.fields,
      });
      toast.success('Template duplicated');
    } catch {
      toast.error('Could not duplicate template');
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteMut.mutateAsync(pendingDelete.id);
      toast.success('Template deleted');
    } catch {
      toast.error('Could not delete template');
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">Export</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Build custom employee exports and save reusable templates.
          </p>
        </div>
        {templates.length > 0 && (
          <Button onClick={onNew} className="rounded-full">
            <Plus className="mr-2 h-4 w-4" />
            New template
          </Button>
        )}
      </div>

      {templates.length === 0 ? (
        <EmptyState onNew={onNew} />
      ) : (
        <div className="space-y-8">
          {templates.length > 6 && (
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search templates…"
                className="pl-9"
              />
            </div>
          )}
          {noMatches && (
            <p className="py-10 text-center text-sm text-muted-foreground">
              No templates match “{query}”.
            </p>
          )}
          <TemplateSection title="My templates" templates={personal} onEdit={onEdit} onDuplicate={duplicate} onDelete={setPendingDelete} />
          {shared.length > 0 && (
            <TemplateSection
              title="Shared templates"
              icon={<Users className="h-4 w-4 text-muted-foreground" />}
              templates={shared}
              onEdit={onEdit}
              onDuplicate={duplicate}
              onDelete={setPendingDelete}
            />
          )}
        </div>
      )}

      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{pendingDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This export template will be removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface SectionProps {
  title: string;
  icon?: React.ReactNode;
  templates: ExportTemplate[];
  onEdit: (t: ExportTemplate) => void;
  onDuplicate: (t: ExportTemplate) => void;
  onDelete: (t: ExportTemplate) => void;
}

function TemplateSection({ title, icon, templates, onEdit, onDuplicate, onDelete }: SectionProps) {
  if (templates.length === 0) return null;
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {icon}
        {title}
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((t) => (
          <TemplateCard key={t.id} template={t} onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
        ))}
      </div>
    </section>
  );
}

interface CardProps {
  template: ExportTemplate;
  onEdit: (t: ExportTemplate) => void;
  onDuplicate: (t: ExportTemplate) => void;
  onDelete: (t: ExportTemplate) => void;
}

function TemplateCard({ template, onEdit, onDuplicate, onDelete }: CardProps) {
  const [exporting, setExporting] = useState(false);
  const canDownload = usePermission('export.download');

  const run = async () => {
    setExporting(true);
    try {
      await runExport({ templateId: template.id, format: 'xlsx' });
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="group flex flex-col rounded-xl border border-border bg-card p-4 transition-shadow hover:shadow-sm">
      <div className="mb-3 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <FileSpreadsheet className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-medium text-foreground">{template.name}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {template.fields.length} column{template.fields.length === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      <div className="mt-auto flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 rounded-full"
          disabled={exporting || !canDownload}
          onClick={run}
          title={canDownload ? undefined : 'You lack the “Download data exports” permission'}
        >
          {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          Export
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full" aria-label="More actions">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(template)}>
              <Pencil className="mr-2 h-4 w-4" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDuplicate(template)}>
              <Copy className="mr-2 h-4 w-4" /> Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(template)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <Download className="h-7 w-7 text-muted-foreground" />
      </div>
      <h3 className="text-base font-medium text-foreground">No export templates yet</h3>
      <p className="mt-1 max-w-xs text-sm text-muted-foreground">
        Create your first template to pull employee data into Excel.
      </p>
      <Button onClick={onNew} className="mt-5 rounded-full">
        <Plus className="mr-2 h-4 w-4" />
        New template
      </Button>
    </div>
  );
}
